import type { ReactNode } from 'react';
import { cn } from './cn';
import { accent } from './accent';
import type { AccentName } from './accent';

export interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  /** Say what the user can do next, not just that something is missing. */
  description?: string;
  action?: ReactNode;
  /** Tints the illustration. Defaults to blue. */
  tone?: AccentName;
  /** `plain` for empties inside a card that already has its own frame. */
  variant?: 'illustrated' | 'plain';
  /**
   * Heading level. `h3` suits an empty slot inside a page that already has its
   * own `h1`; pass `h1` when this state *replaces* the whole page — an error or
   * not-found screen still needs a top-level heading.
   */
  as?: 'h1' | 'h2' | 'h3';
  className?: string;
}

/**
 * The illustration is drawn from the same shapes as the logo — a rising bar
 * chart with a connecting line above it. It is inline SVG rather than an asset:
 * it costs no request, it inherits the accent colour, and it stays crisp at any
 * size. Purely decorative, so it is hidden from assistive tech.
 */
const Illustration = ({ tone }: { tone: AccentName }) => {
  const palette = accent(tone);
  return (
    <div
      aria-hidden="true"
      className={cn('mb-6 flex h-28 w-36 items-end justify-center gap-2 rounded-lg px-5 pb-5', palette.soft)}
    >
      {[36, 58, 44, 72].map((height, index) => (
        <span
          key={height}
          className={cn(
            'w-4 rounded-t-sm',
            // The tallest bar is full strength; the rest step back so the group
            // reads as one object rather than four competing blocks.
            index === 3 ? palette.rule : `${palette.rule} opacity-40`,
          )}
          style={{ height: `${height}%` }}
        />
      ))}
    </div>
  );
};

export const EmptyState = ({
  icon,
  title,
  description,
  action,
  tone = 'blue',
  variant = 'illustrated',
  as: Heading = 'h3',
  className,
}: EmptyStateProps) => {
  const palette = accent(tone);

  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-16 text-center', className)}>
      {variant === 'illustrated' && !icon ? (
        <Illustration tone={tone} />
      ) : icon ? (
        <div
          className={cn(
            'mb-5 flex h-14 w-14 items-center justify-center rounded-xl',
            variant === 'illustrated' ? cn(palette.soft, palette.text) : 'bg-background-alt text-muted-foreground',
          )}
        >
          {icon}
        </div>
      ) : null}

      <Heading className="font-sans text-h4 font-semibold text-text-primary">{title}</Heading>
      {description && <p className="mt-2 max-w-sm text-caption text-text-secondary">{description}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
};

export default EmptyState;
